import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthState } from "@/context";

import { cn } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, MoreVertical, SquarePen } from "lucide-react";

import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { forwardRef, useRef, useState } from "react";
import ChangeRole from "./common/ChangeRole";
import { TbUserCheck, TbUsersPlus, TbUserCancel } from "react-icons/tb";
import Deactivate from "./common/Deactivate";
import Reactivate from "./common/Reactivate";
import UpdateLimitAmount from "./common/UpdateLimitAmount";

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  email: string;
  status: "active" | "inactive";
};

export const columnsT: ColumnDef<User>[] = [
  {
    accessorKey: "fullName",
    header: ({ column }) => {
      return <div>Full Name</div>;
    },
    cell: ({ row }) => (
      <div>
        {row?.original?.merchant?.firstName} {row?.original?.merchant?.lastName}
      </div>
    ),
  },
  {
    accessorKey: "roleId",
    header: ({ column }) => {
      return <div>Role</div>;
    },
    cell: ({ row }) => (
      <div className="capitalize">{row?.original?.roleId?.split("-")[1]}</div>
    ),
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return <div>Email</div>;
    },
    cell: ({ row }) => (
      <div className="lowercase">{row?.original?.merchant?.email}</div>
    ),
  },
  {
    accessorKey: "phone",
    header: () => <div className="">Phone Number</div>,
    cell: ({ row }) => {
      return <div className="">{row?.original?.merchant?.phone}</div>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <div
        className={cn(
          "capitalize flex justify-center items-center rounded-[37px] py-1 px-2",
          row.getValue("status") === "active"
            ? "bg-green0 text-green-3"
            : "bg-saffron-10 text-saffron"
        )}
      >
        {row.getValue("status")}
      </div>
    ),
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      return <DropdownWithDialogItemsSolution2 data={row?.original} />;
    },
  },
];
export const columnsAsset: ColumnDef<User>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => {
      return <div>Currency</div>;
    },
    cell: ({ row }) => (

      <div className="flex items-center gap-2">
        <img src={row?.original?.currency?.icon} className="w-6 h-6" />
        {row?.original?.currency?.name}
      </div>
    ),
  },
  {
    accessorKey: "code",
    header: ({ column }) => {
      return <div>Code</div>;
    },
    cell: ({ row }) => (
      <div className="capitalize">
        {row?.original?.currency?.code}
      </div>
    ),
  },
  {
    accessorKey: "minDeposit",
    header: ({ column }) => {
      return <div>Minimum Depsosit </div>;
    },
    cell: ({ row }) => (
      <div >
        {row?.original?.currency?.type !== 'crypto' && row?.original?.currency?.code} {row?.original?.currency?.minDeposit} {row?.original?.currency?.type !== 'fiat' && row?.original?.currency?.code}
      </div>
    ),
  },
  {
    id: "actions",
    enableHiding: false,
    header: ({ column }) => {
      return <div>Action </div>;
    },
    cell: ({ row }) => {
      return <UpdateLimitAmount data={row?.original} />;
      // <DropdownWithDialogItemsSolution data={row?.original} />;
    },
  },
];

function DropdownWithDialogItemsSolution2({ data }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasOpenDialog, setHasOpenDialog] = useState(false);
  const dropdownTriggerRef = useRef(null);
  const focusRef = useRef(null);
  const { state } = useAuthState();

  function handleDialogItemSelect() {
    focusRef.current = dropdownTriggerRef.current;
  }

  function handleDialogItemOpenChange(open) {
    setHasOpenDialog(open);
    if (open === false) {
      setDropdownOpen(false);
    }
  }
  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger
        asChild
        disabled={data?.roleId === "merchant-owner" ? true : false}
      >
        <Button
          variant="ghost"
          className="w-8 h-8 p-0"
          ref={dropdownTriggerRef}
        >
          <span className="sr-only">Open menu</span> <MoreVertical />{" "}
        </Button>
      </DropdownMenuTrigger>

      {state?.merchant?.roleId === "merchant-owner" ||
        state?.merchant?.roleId === "merchant-admin" ? (
        <DropdownMenuContent
          sideOffset={5}
          hidden={hasOpenDialog}
          onCloseAutoFocus={(event) => {
            if (focusRef.current) {
              focusRef.current.focus();
              focusRef.current = null;
              event.preventDefault();
            }
          }}
        >
          <DialogItem
            triggerChildren={
              <p className="flex items-center gap-2 text-xs text-primary0 ">
                <TbUsersPlus />
                Update Role
              </p>
            }
            onSelect={handleDialogItemSelect}
            onOpenChange={handleDialogItemOpenChange}
            size="sm"
          >
            <ChangeRole
              id={data?.id}
              name={`${data?.merchant?.firstName} ${data?.merchant?.lastName}`}
              handleDialogItemOpenChange={handleDialogItemOpenChange}
            />
          </DialogItem>
          <DialogItem
            triggerChildren={
              <>
                {data?.status !== "active" ? (
                  <p className="flex items-center gap-2 text-xs text-green-3 hover:text-green-3">
                    <TbUserCheck /> Reactivate
                  </p>
                ) : (
                  <p className="flex items-center gap-2 text-xs text-error hover:text-error">
                    <TbUserCancel /> Deactivate
                  </p>
                )}
              </>
            }
            onSelect={handleDialogItemSelect}
            onOpenChange={handleDialogItemOpenChange}
            size="sm"
          >
            {data?.status === "active" ? (
              <Deactivate
                id={data?.id}
                name={`${data?.firstName} ${data?.lastName}`}
                handleDialogItemOpenChange={handleDialogItemOpenChange}
              />
            ) : (
              <Reactivate
                id={data?.id}
                name={`${data?.firstName} ${data?.lastName}`}
                handleDialogItemOpenChange={handleDialogItemOpenChange}
              />
            )}
          </DialogItem>
        </DropdownMenuContent>
      ) : null}
    </DropdownMenu>
  );
}

const DialogItem = forwardRef((props, forwardedRef) => {
  const {
    triggerChildren,
    children,
    onSelect,
    onOpenChange,
    size,
    ...itemProps
  } = props;
  return (
    <Dialog onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          {...itemProps}
          ref={forwardedRef}
          className="cursor-pointer"
          onSelect={(event) => {
            event.preventDefault();
            onSelect && onSelect();
          }}
        >
          {triggerChildren}
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent
        className={cn(
          "p-0 text-left items-start rounded-[4px] bg-background",
          size ? `max-w-${size}` : "max-w-xs"
        )}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
});

